import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';

import { sampleLabelOf } from '@/shared/api/sample-data';

/**
 * "Dit is voorbeelddata."
 *
 * Een paar onderdelen hebben nog geen echte gegevens; die schermen tonen dan
 * voorbeelddata (zie `shared/api/sample-data.ts`). Deze melding staat boven het
 * werkvlak zolang een scherm in beeld zulke data gebruikt, met de naam van het
 * onderdeel erbij - zodat niemand een verzonnen cijfer voor een echt cijfer
 * aanziet. Amber en rustig: het is geen fout van de gebruiker.
 */
export function SampleDataNotice() {
    const labels = useSampleLabels();

    if (labels.length === 0) {
        return null;
    }

    return (
        <p
            role="status"
            className="m-0 border-b border-[var(--warn-border)] bg-[var(--warn-tint)] px-5 py-2 text-[13px] leading-[1.45] text-[var(--warn-text)]"
        >
            <strong className="font-medium">Voorbeelddata</strong>
            {' · '}
            {labels.join(', ')}
            {labels.length === 1
                ? ' werkt nog niet met echte gegevens. Wat je daar ziet, is een voorbeeld.'
                : ' werken nog niet met echte gegevens. Wat je daar ziet, is een voorbeeld.'}
        </p>
    );
}

/** De labels van voorbeelddata in queries die nu in beeld zijn. */
function useSampleLabels(): string[] {
    const queryClient = useQueryClient();
    const cache = queryClient.getQueryCache();

    const subscribe = useCallback((notify: () => void) => cache.subscribe(notify), [cache]);
    const snapshot = useCallback(
        () =>
            [
                ...new Set(
                    cache
                        .getAll()
                        .filter((query) => query.getObserversCount() > 0)
                        .map((query) => sampleLabelOf(query.state.data))
                        .filter((label): label is string => label !== null),
                ),
            ]
                .sort((left, right) => left.localeCompare(right, 'nl'))
                .join('\n'),
        [cache],
    );

    const joined = useSyncExternalStore(subscribe, snapshot, snapshot);

    return joined === '' ? [] : joined.split('\n');
}
